import {
  createSlice,
  createEntityAdapter,
  createSelector
} from '@reduxjs/toolkit'
import { api, providesList } from '../base'
import socket from '../../socket'
import {
  showSnackbar,
} from '../../../features/snackbar/snackbarSlice'

const transformErrorResponse = (response, meta, arg) => response.error

const roomsAdapter = createEntityAdapter({
  selectId: (room) => room.id,
  sortComparer: (a, b) => a.created_at < b.created_at ? -1 : 1
})
const initialState = roomsAdapter.getInitialState()

export const roomsApi = api
  .enhanceEndpoints({ addTagTypes: ['Room'] })
  .injectEndpoints({
    endpoints: (builder) => ({
      // get room by id
      getRoom: builder.query({
        query: (id) => ({
          url: `/api/rooms/${id}`,
          method: 'GET',
        }),
        transformResponse: (response, meta, arg) => {
          return roomsAdapter.addOne(initialState, response.room)
        },
        transformErrorResponse,
        providesTags: (result, error, id) => [{ type: 'Room', id }],
        async onQueryStarted(
          arg,
          queryLifecycleAPI
        ) {
          //console.log('[getRoom.onQueryStarted]')
        },
        async onCacheEntryAdded(
          arg,
          queryCacheLifecycleAPI
        ) {
          //console.log('[getRoom.onCacheEntryAdded]')
        },
      }),
      // list all rooms
      listRooms: builder.query({
        query: () => ({
          url: '/api/rooms',
          method: 'GET',
        }),
        transformResponse: (response, meta, arg) => {
          //console.log('[listRooms.transformResponse]', response)
          return roomsAdapter.setAll(
            initialState,
            response.rooms.items)
        },
        transformErrorResponse,
        providesTags: (result) => providesList(result?.ids, 'Room'),
        async onQueryStarted(
          arg,
          queryLifecycleAPI
        ) {
          //console.log('[listRooms.onQueryStarted]', queryLifecycleAPI.getState().api.queries[`listRooms(undefined)`].status)
          try {
            await queryLifecycleAPI.queryFulfilled
            //console.log('[listRooms.onQueryStarted] queryFulfilled:', queryLifecycleAPI.getState().api.queries[`listRooms(undefined)`].status)
          } catch {
            console.error('Some error')
          }
        },
        async onCacheEntryAdded(
          arg,
          queryCacheLifecycleAPI
        ) {
          //console.log('[listRooms.onCacheEntryAdded]', queryCacheLifecycleAPI.getState().api.queries[`listRooms(undefined)`].status)
          try {
            await queryCacheLifecycleAPI.cacheDataLoaded
            const create_listener = (data) => {
              //console.log(`[created_room] data =`, data)
              queryCacheLifecycleAPI.updateCachedData((draft) => {
                return roomsAdapter.upsertOne(draft, data.created_room)
              })
            }
            socket.on('created_room', create_listener)
            const update_listener = (data) => {
              console.log(`[listRooms.updated_room] data =`, data)
              queryCacheLifecycleAPI.updateCachedData((draft) => {
                return roomsAdapter.upsertOne(draft, data.updated_room)
              })
            }
            socket.on('updated_room', update_listener)
          } catch (err) {
            console.error(err)
          }

          await queryCacheLifecycleAPI.cacheEntryRemoved
          socket.off('created_room')
          socket.off('updated_room')
        },
      }),
      // create room (pessimistic)
      createRoom: builder.mutation({
        query: ({ name, emails }) => ({
          url: '/api/rooms',
          method: 'POST',
          body: { name, emails }
        }),
        transformResponse: (response, meta, args) => {
          return roomsAdapter.addOne(initialState, response.created_room)
        },
        async onQueryStarted({ name, emails }, { dispatch, queryFulfilled }) {
          try {
            const { data: { ids, entities } } = await queryFulfilled
            //console.log('[createRoom] entities =', entities)
            const createResult = dispatch(
              api.util.updateQueryData('listRooms', undefined, (draft) => {
                return roomsAdapter.addOne(draft, entities[ids[0]])
              })
            )
          } catch (err) {
            console.error(err)
          }
        },
      }),
      // leave room (optimistic)
      leaveRoom: builder.mutation({
        query: (id) => ({
          url: `/api/rooms/${id}`,
          method: 'DELETE',
        }),
        transformErrorResponse,
        async onQueryStarted(id, { dispatch, queryFulfilled }) {
          const deleteResult = dispatch(
            api.util.updateQueryData('listRooms', undefined, (draft) => {
              return roomsAdapter.removeOne(draft, id)
            })
          )
          try {
            await queryFulfilled
            // SOCKET LEAVE ROOM
            socket.emit('leave', { room_id: id })
          } catch (err) {
            console.error(err)
            deleteResult.undo()
          }
        }
      }),
      // add members to room (optimistically adds email and is_anonymous, then
      // fetches the rest through invalidation)
      addMembersToRoom: builder.mutation({
        query: ({ id, invitees }) => ({
          url: `/api/rooms/${id}/users`,
          method: 'PATCH',
          body: { emails: invitees.map(m => m.email) }
        }),
        transformErrorResponse,
        invalidatesTags: (result, error, arg) => [{ type: 'Room', id: arg.id }],
        async onQueryStarted({ id, invitees }, { dispatch, getState, queryFulfilled }) {
          const room = getState().api.queries['listRooms(undefined)'].data.entities[id]
          const addMembersResult = dispatch(
            api.util.updateQueryData('listRooms', undefined, (draft) => {
              const changes = { users: [...invitees, ...room.users] }
              return roomsAdapter.updateOne(draft, {id, changes})
            })
          )
          try {
            await queryFulfilled
            dispatch(showSnackbar({
              severity: 'success',
              message: 'New members added to channel',
            }))
          } catch {
            addMembersResult.undo()
            dispatch(showSnackbar({
              severity: 'error',
              message: 'There was an error adding new members',
            }))
          }
        }
      })
      //

    }),
  })

export const {
  useGetRoomQuery,
  useListRoomsQuery,
  useCreateRoomMutation,
  useLeaveRoomMutation,
  useAddMembersToRoomMutation,
} = roomsApi

export const selectListRoomsResult = roomsApi.endpoints.listRooms.select()

const selectRoomsData = createSelector(
  [
    selectListRoomsResult,
  ],
  (roomsResult) => {
    return roomsResult.data
  }
)

export const {
  selectAll: selectAllRooms,
  selectById: selectRoomById } = roomsAdapter.getSelectors((state) => {
    return selectRoomsData(state) ?? initialState
  })
